import { maybeWebContext } from "../search";
// chat/brain.ts — the chat brain, SERVER-FIRST since 2026-07-02 (the R8 graduation).
// askBrain now sends the message + this app's declared action catalog to the generic
// fleet chat brain (/api/apps/expense_audit_ai/chat) — ONE billed call returns ONE
// registered action + a reply; the app executes it locally so data stays on-device.
// The regex intent-router below is the FREE FALLBACK (offline / signed-out / out of
// funds) — chat never goes dead. Original prototype notes:
//
// It maps ONE user message → ONE
// ChatAction, reusing ONLY endpoints that are ALREADY live (no new/edited server routes, no deploy):
//   • "add expense … / spent … / paid …"     → existing /structure  (structureReceipt) → add_expense
//   • "show / find / how much / over $…"       → existing /query      (parseExpenseQuery) → filter
//   • "add a category X"                       → light client intent  → add_category
//   • "summar… / overview"                     → local dashboardSummary → summarize
//   • "delete / remove <vendor>"               → resolve id locally    → delete_expense (UI confirms)
//   • "approve/reject/mark/set … category/amount" → resolve id locally → edit_expense
//   • anything else                            → none + a short clarifying nudge
//
// A proper SERVER chat-brain that returns the action in one billed call is the productionized version
// (OUT OF SCOPE now). This deliberately keeps AI calls scoped to the two intents that truly need them,
// so idle chit-chat never bills.

import { BrainError, structureReceipt, parseExpenseQuery, brainCall } from "../brain";
import { store, type Expense, type ExpenseFilter, type ExpensePatch, type ExpenseStatus } from "../store";
import { findNamedMatches, notFoundMessage } from "./fuzzy";
import type { ChatAction, FilterRow } from "./actions";
import { libraryFind, librarySummary } from "../library";

export interface ChatTurn {
  role: "user" | "assistant";
  content: string;
  rows?: FilterRow[]; // assistant turns may carry filter matches to render inline
}
// One resolved action (what toAction/routeMessage build).
export interface BrainResponse {
  reply: string;      // conversational preface; executeAction supplies the result line
  action: ChatAction;
}
// askBrain assembles the ARRAY — two asks in one message = two actions (G17); the server chat
// brain returns an `actions` array, so this also hardens the app against that response shape.
export interface BrainResult {
  reply: string;
  actions: ChatAction[];
}

const todayISO = () => new Date().toISOString().slice(0, 10);

const HELP =
  'I can add expenses, add categories, search/filter, summarize, and delete — just tell me. Try:\n' +
  '• "add expense Starbucks $4.50 today"\n' +
  '• "show travel over $500 this month"\n' +
  '• "add a category Software"\n' +
  '• "summarize this month"\n' +
  '• "approve Uber" · "delete Starbucks"';

// Anything query-shaped routes to the (billed) /query parser.
const QUERY_RE = /\b(show|find|list|search|which|filter|how\s+much|how\s+many|total|spent|over|under|above|below|more\s+than|less\s+than|between|this\s+month|last\s+month|this\s+year|last\s+year|pending|approved|rejected|expenses?)\b/i;

function parseCents(s: string): number | null {
  const n = parseFloat(s.replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? Math.round(n * 100) : null;
}

async function findByVendor(term: string): Promise<Expense[]> {
  const q = term.trim().toLowerCase().replace(/[.?!]+$/, "");
  if (!q) return [];
  const all = await store.listExpenses({ limit: 50000 });
  const subs = all.filter((e) => e.vendor.toLowerCase().includes(q));
  if (subs.length) return subs;
  // Fleet FUZZY standard (fuzzy.ts): forgiving near-match on the vendor name so a mistyped or
  // paraphrased merchant still resolves ("starbcks" → "Starbucks"). Client-side; data on-device.
  const named = findNamedMatches(all.map((e) => ({ ...e, name: e.vendor })), term);
  return named.map(({ name: _n, ...e }) => e as Expense);
}

function pickList(rows: Expense[]): string {
  const money = (c: number, cur = "USD") =>
    new Intl.NumberFormat(undefined, { style: "currency", currency: cur, currencyDisplay: "narrowSymbol" }).format(c / 100);
  return rows
    .slice(0, 5)
    .map((e) => `• ${e.vendor} — ${money(e.amount_cents, e.currency)} (${e.date_on})`)
    .join("\n");
}

export async function askBrain(history: ChatTurn[], onChunk?: (t: string) => void): Promise<BrainResult> {
  const last = [...history].reverse().find((t) => t.role === "user");
  const text = (last?.content ?? "").trim();
  if (!text) { const r = await routeMessage(text); return { reply: r.reply, actions: [r.action] }; }
  try {
    return await serverChat(history, text, onChunk);
  } catch (e) {
    // A money/auth/key refusal must be SAID, not masked: the local fallback answering a
    // funds-blocked question as if it were a search reads as a broken app (found live 2026-08-27).
    if (e instanceof BrainError && (e.code === "insufficient_funds" || e.code === "unauthenticated" || e.code === "managed_ai_blocked")) {
      return { reply: e.message, actions: [{ kind: "none" }] };
    }
    // Offline / transient server trouble → the free local intent-router below
    // still answers (single action); chat never goes dead.
    const r = await routeMessage(text);
    return { reply: r.reply, actions: [r.action] };
  }
}

// ---- SERVER CHAT-BRAIN (the graduated path) --------------------------------
// The app declares WHAT it can do; the fleet chat brain decides WHICH action the
// message means; this file resolves references (vendor → row id, category name →
// id) locally, because the data lives here.

export const CHAT_ACTIONS = [
  { name: "add_expense", description: "Add one expense row to the ledger.", params: { vendor: "short name for what it was (Coffee, Uber, Rent) — INFER from the user's words, never ask if guessable", amount_cents: "integer cents — CONVERT from natural money yourself ('$20' / '20 dollars' → 2000); NEVER ask the user for cents", date_on: "YYYY-MM-DD — resolve 'yesterday' / 'last Friday' from CURRENT DATE", category: "optional category name from APP DATA", notes: "optional short note", currency: "optional 3-letter ISO code ONLY when the user names one ('euros'→EUR, 'pounds'→GBP); omit otherwise" } },
  { name: "add_category", description: "Create a new expense category.", params: { name: "category name", color: "optional #rrggbb hex" } },
  { name: "rename_category", description: "Rename an existing category — all its expenses follow.", params: { from: "current category name (from APP DATA)", to: "the new name" } },
  { name: "delete_category", description: "Delete a category; its expenses stay, just uncategorized.", params: { name: "category name (from APP DATA)" } },
  { name: "recategorize", description: "Move MANY expenses at once into a category (e.g. 'put all Starbucks under Coffee'). Creates the target category if needed.", params: { to_category: "target category name", vendor_contains: "optional name/merchant text to match", from_category: "optional current category to move from" } },
  { name: "edit_expense", description: "Change one existing expense; identify it by its vendor/merchant name.", params: { vendor: "which expense (merchant name)", set_status: "optional audit mark: pending (unreviewed) | approved (reviewed) | rejected (flagged)", set_category: "optional category name from APP DATA", set_amount_cents: "optional corrected integer cents — CONVERT from natural money yourself", set_notes: "optional replacement note" } },
  { name: "delete_expense", description: "Delete one expense identified by its vendor/merchant name (the app asks the user to confirm first).", params: { vendor: "which expense (merchant name)" } },
  { name: "filter", description: "Search / filter the expense list.", params: { vendor_contains: "optional merchant text", category: "optional category name from APP DATA", status: "optional pending (unreviewed) | approved (reviewed) | rejected (flagged)", from_date: "optional YYYY-MM-DD", to_date: "optional YYYY-MM-DD", min_cents: "optional integer cents (convert from natural money)", max_cents: "optional integer cents (convert from natural money)" } },
  { name: "summarize", description: "Summarize a month of spending.", params: { month: "optional YYYY-MM (omit for the current month)" } },
  { name: "set_default_currency", description: "Change the default currency for new expenses.", params: { currency: "3-letter code like USD, EUR, SGD" } },
  { name: "backup_save", description: "Back up the WHOLE expense library to the customer's own GitHub or Google Drive (connected once in their Oriora account). Works for ANY size — if the backup is too large for the standard path (receipt photos), it saves straight from this device to their GitHub when that's logged in (backup menu), or the reply tells them the one-time login to enable it.", params: {} },
  { name: "backup_status", description: "Tell the customer WHERE their data is backed up: which cloud is connected in their Oriora account, which one new backups write to, and when the last backup was. Use for 'where is my data / is my stuff backed up / which cloud do you use'.", params: {} },
  { name: "backup_restore", description: "Restore the library from the customer's cloud backup — REPLACES current data (the app asks the user to confirm first). If they NAME a source ('restore from Drive'), pass provider.", params: { provider: "optional github|gdrive when the customer names which cloud to restore from" } },
  { name: "save_to_cloud", description: "Save a note, or THIS conversation, to the customer's OWN GitHub or Google Drive. Use when they ask to store/save/keep the conversation, their prompts/messages, or a note (NOT the whole expense library — that's backup_save).", params: { content_kind: "\"conversation\" to save this whole chat, or \"note\" for a piece of text", note: "the text to save when content_kind is \"note\"", destination: "optional \"github\" or \"gdrive\" if the user names one; omit otherwise" } },
  { name: "add_recurring", description: "Create a RECURRING expense that auto-adds itself ('rent 1200 every month on the 1st', 'gym $30 weekly on Mondays').", params: { vendor: "short name (Rent, Gym…)", amount_cents: "integer cents — CONVERT from natural money yourself", frequency: "monthly | weekly", day: "monthly: day of month 1-28 · weekly: 0-6 with Sunday=0", category: "optional category name from APP DATA" } },
  { name: "list_recurring", description: "Show the recurring expense rules.", params: {} },
  { name: "delete_recurring", description: "Stop a recurring expense; identify it by its name.", params: { vendor: "which recurring rule (its name)" } },
  { name: "remember", description: "Save a standing instruction for how THIS customer wants the app to behave from now on (e.g. 'always put coffee under Food', 'default currency EUR').", params: { instruction: "the preference, one sentence" } },
  // ── ARTIFACT TOOLS — the customer's own library (device-only). Whatever the AI
  // makes for them gets KEPT here instead of vanishing into the chat. Fleet-wide
  // capability, identical in every Oriora app.
  { name: "library_save", description: "Save a plan, summary, guide or note into the customer's ON-DEVICE library. Use whenever you produce something worth keeping, or they ask you to save/remember/write something down. When they ask you to RESEARCH something and make them a plan: write the FULL text in \`content\`, give it a clear title, and put a short summary in your reply.", params: { title: "short title", item_kind: "lesson | plan | note", content: "the FULL text to save — write it out completely", folder: "optional folder name" } },
  { name: "library_list", description: "List what the customer has saved in their library ('what have I saved', 'show my plans', 'what's in my library / in folder X').", params: { folder: "optional folder to filter by" } },
  { name: "library_remove", description: "Remove ONE library item by its title (the app asks the customer to confirm first). LIBRARY items only — the app's own entries have their own delete action.", params: { title: "which item (its title, from LIBRARY in the context)" } },
  { name: "library_move", description: "Move a library item into a folder ('move the summer plan into Plans').", params: { title: "which item", folder: "target folder name" } },
  { name: "library_merge", description: "Merge two or more library items into one (the app asks the customer to confirm first).", params: { titles: "the titles to merge, separated by |", new_title: "title for the merged item" } },
];

// Standing instructions: the customer shapes how the app behaves through chat; stored on-device,
// included in every brain call. "The app keeps its look — how it's used is theirs to define."
const INSTRUCTIONS_KEY = "oriora.expense.instructions";
export const getInstructions = (): string => { try { return localStorage.getItem(INSTRUCTIONS_KEY) ?? ""; } catch { return ""; } };
export const addInstruction = (line: string): void => { try { const cur = getInstructions(); localStorage.setItem(INSTRUCTIONS_KEY, cur ? `${cur}\n- ${line}` : `- ${line}`); } catch { /* ignore */ } };

interface ServerAct { name?: string; action?: string; params?: Record<string, unknown> }
interface ServerChat { actions?: ServerAct[]; action?: string; params?: Record<string, unknown>; reply?: string }

async function serverChat(history: ChatTurn[], text: string, onChunk?: (t: string) => void): Promise<BrainResult> {
  const cats = await store.listCategories();
  let defCur = "USD"; try { defCur = localStorage.getItem("oriora.expense.currency") || "USD"; } catch { /* ignore */ }
  const notes = getInstructions();
  const context = `LIBRARY (their saved plans/notes): ${librarySummary()}. Categories: ${cats.map((c) => c.name).join(", ") || "(none yet)"}. Default currency: ${defCur}.` +
    (notes ? `\nCustomer's standing instructions (apply when filling params): ${notes}` : "");
  // 20 turns (the server's cap) — 8 made the brain forget a pasted list after ~4 rounds of "next".
  const recent = history.slice(-21, -1).map((t) => ({ role: t.role, content: t.content }));
  const r = await brainCall<ServerChat>("chat", { message: text + (await maybeWebContext(text)), actions: CHAT_ACTIONS, context, history: recent }, onChunk);
  // The shared brain returns an ARRAY (G17): "coffee $4 and delete the Uber" = two actions. Fall
  // back to the single-action shape for an older server.
  const say = asStr(r.reply) ?? "";
  const raw: ServerAct[] = Array.isArray(r.actions) && r.actions.length
    ? r.actions
    : (r.action ? [{ action: r.action, params: r.params }] : []);
  if (!raw.length) return { reply: say || HELP, actions: [{ kind: "none" }] };
  const actions: ChatAction[] = [];
  for (const a of raw) {
    const one = await toAction({ action: a.name ?? a.action ?? "", params: a.params, reply: "" });
    actions.push(one.action);
  }
  return { reply: say, actions };
}

const asStr = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.trim() : undefined);
const asCents = (v: unknown): number | undefined => {
  const n = typeof v === "number" ? v : parseFloat(String(v ?? ""));
  return Number.isFinite(n) && n > 0 ? Math.round(n) : undefined;
};
const STATUSES = new Set<ExpenseStatus>(["pending", "approved", "rejected"]);
const none = (message: string): BrainResponse => ({ reply: "", action: { kind: "none", message } });

// Resolve a vendor reference to exactly one local row, or explain why not.
async function resolveOne(term: string | undefined): Promise<{ row?: Expense; problem?: string }> {
  if (!term) return { problem: "Which expense do you mean? Give me the vendor name." };
  const rows = await findByVendor(term);
  if (rows.length === 0) {
    const all = await store.listExpenses({ limit: 50000 });
    return { problem: notFoundMessage(all.map((e) => ({ name: e.vendor })), term) };
  }
  if (rows.length > 1) return { problem: `I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}` };
  return { row: rows[0] };
}

export async function toAction(r: ServerChat): Promise<BrainResponse> {
  const p = r.params ?? {};
  const say = asStr(r.reply) ?? "";

  // ── ARTIFACT TOOLS (the library) — handled EARLY, above any app-specific
  // precondition (no vehicle / no plant / nothing selected). The library belongs
  // to the customer, not to a currently-active record. (Garage shipped this bug:
  // its no-vehicle guard silently swallowed every library action, 2026-08-13.)
  if (r.action === "library_save") {
    const _content = asStr(p.content);
    if (!_content) return none("Tell me what to save — or ask me to write it first and I'll keep it.");
    const _rawKind = (asStr(p.item_kind) ?? "note").toLowerCase();
    const _itemKind = _rawKind === "lesson" || _rawKind === "plan" ? _rawKind : "note";
    return { reply: say, action: { kind: "library_save", title: asStr(p.title) ?? "Untitled", itemKind: _itemKind, content: _content, folder: asStr(p.folder) } };
  }
  if (r.action === "library_list") return { reply: say, action: { kind: "library_list", folder: asStr(p.folder) } };
  if (r.action === "library_remove") {
    const _r = resolveLibraryItem(asStr(p.title));
    if (!_r.item) return none(_r.problem!);
    return { reply: say, action: { kind: "library_remove", id: _r.item.id, label: `"${_r.item.title}"` } };
  }
  if (r.action === "library_move") {
    const _folder = asStr(p.folder);
    if (!_folder) return none("Which folder should it go to?");
    const _r2 = resolveLibraryItem(asStr(p.title));
    if (!_r2.item) return none(_r2.problem!);
    return { reply: say, action: { kind: "library_move", id: _r2.item.id, folder: _folder, label: `"${_r2.item.title}"` } };
  }
  if (r.action === "library_merge") {
    const _titles = (asStr(p.titles) ?? "").split("|").map((s) => s.trim()).filter(Boolean);
    if (_titles.length < 2) return none("Tell me which two (or more) items to merge, by title.");
    const _ids: string[] = [];
    for (const _t of _titles) {
      const _r3 = resolveLibraryItem(_t);
      if (!_r3.item) return none(_r3.problem!);
      _ids.push(_r3.item.id);
    }
    return { reply: say, action: { kind: "library_merge", ids: _ids, title: asStr(p.new_title) ?? _titles.join(" + "), label: _titles.map((t) => `"${t}"`).join(" + ") } };
  }
  switch (r.action) {
    case "add_expense": {
      const vendor = asStr(p.vendor);
      const cents = asCents(p.amount_cents);
      if (!vendor || !cents) return none(say || 'I need at least a vendor and an amount — try "add expense Starbucks $4.50 today".');
      return {
        reply: say,
        action: { kind: "add_expense", vendor, amount_cents: cents, date_on: asStr(p.date_on) ?? todayISO(), currency: (asStr(p.currency)?.toUpperCase() ?? null), category: asStr(p.category) ?? null, notes: asStr(p.notes) ?? null },
      };
    }
    case "add_category": {
      const name = asStr(p.name);
      if (!name) return none(say || "What should the category be called?");
      return { reply: say, action: { kind: "add_category", name, color: asStr(p.color) } };
    }
    case "edit_expense": {
      const { row, problem } = await resolveOne(asStr(p.vendor));
      if (!row) return none(problem!);
      const patch: ExpensePatch = {};
      const status = asStr(p.set_status)?.toLowerCase() as ExpenseStatus | undefined;
      if (status && STATUSES.has(status)) patch.status = status;
      const cents = asCents(p.set_amount_cents);
      if (cents) patch.amount_cents = cents;
      const noteText = asStr(p.set_notes);
      if (noteText !== undefined) patch.notes = noteText;
      const catName = asStr(p.set_category);
      if (catName) {
        const cat = (await store.listCategories()).find((c) => c.name.toLowerCase() === catName.toLowerCase());
        if (!cat) return none(`There's no category called "${catName}" — add it first or pick an existing one.`);
        patch.category_id = cat.id;
      }
      if (Object.keys(patch).length === 0) return none(say || "What should I change on it — status, category, amount, or note?");
      return { reply: say, action: { kind: "edit_expense", id: row.id, patch, label: row.vendor } };
    }
    case "delete_expense": {
      const { row, problem } = await resolveOne(asStr(p.vendor));
      if (!row) return none(problem!);
      const money = new Intl.NumberFormat(undefined, { style: "currency", currency: row.currency, currencyDisplay: "narrowSymbol" }).format(row.amount_cents / 100);
      return { reply: say, action: { kind: "delete_expense", id: row.id, label: `${row.vendor} (${money})` } };
    }
    case "filter": {
      const filter: ExpenseFilter = {};
      const vendorText = asStr(p.vendor_contains);
      if (vendorText) filter.vendor_contains = vendorText;
      const catName = asStr(p.category);
      if (catName) filter.category = catName;
      const status = asStr(p.status)?.toLowerCase() as ExpenseStatus | undefined;
      if (status && STATUSES.has(status)) filter.status = status;
      const from = asStr(p.from_date);
      if (from) filter.from_date = from;
      const to = asStr(p.to_date);
      if (to) filter.to_date = to;
      const min = asCents(p.min_cents);
      if (min) filter.min_cents = min;
      const max = asCents(p.max_cents);
      if (max) filter.max_cents = max;
      if (Object.keys(filter).length === 0) return none(say || "What should I filter by — vendor, category, status, dates, or amount?");
      return { reply: "", action: { kind: "filter", filter, label: say || undefined } };
    }
    case "summarize":
      return { reply: say, action: { kind: "summarize", month: asStr(p.month) } };
    case "rename_category": {
      const from = asStr(p.from), to = asStr(p.to);
      if (!from || !to) return none(say || "Which category, renamed to what?");
      return { reply: say, action: { kind: "rename_category", from, to } };
    }
    case "delete_category": {
      const name = asStr(p.name);
      if (!name) return none(say || "Which category should I delete?");
      return { reply: say, action: { kind: "delete_category", name } };
    }
    case "recategorize": {
      const to_category = asStr(p.to_category);
      if (!to_category) return none(say || "Which category should they move to?");
      return { reply: say, action: { kind: "recategorize", to_category, vendor_contains: asStr(p.vendor_contains), from_category: asStr(p.from_category) } };
    }
    case "set_default_currency": {
      const currency = asStr(p.currency)?.toUpperCase();
      if (!currency || !/^[A-Z]{3}$/.test(currency)) return none(say || "Which currency — a 3-letter code like USD or EUR?");
      return { reply: say, action: { kind: "set_default_currency", currency } };
    }
    case "backup_save":
      return { reply: say, action: { kind: "backup_save" } };
    case "add_recurring": {
      const vendor = asStr(p.vendor);
      const cents = asCents(p.amount_cents);
      if (!vendor || !cents) return none(say || 'Tell me what and how much — e.g. "rent 1200 every month on the 1st".');
      const frequency = asStr(p.frequency)?.toLowerCase() === "weekly" ? "weekly" as const : "monthly" as const;
      const dayN = Number(p.day);
      const day = Number.isFinite(dayN) ? Math.round(dayN) : frequency === "weekly" ? 1 : 1;
      return { reply: say, action: { kind: "add_recurring", vendor, amount_cents: cents, frequency, day, category: asStr(p.category) ?? null } };
    }
    case "list_recurring":
      return { reply: say, action: { kind: "list_recurring" } };
    case "delete_recurring": {
      const vendor = asStr(p.vendor);
      if (!vendor) return none(say || "Which recurring expense should I stop?");
      return { reply: say, action: { kind: "delete_recurring", vendor } };
    }
    case "backup_status":
      return { reply: say, action: { kind: "backup_status" } };
    case "backup_restore": {
      const prov = asStr(p.provider);
      return { reply: say, action: { kind: "backup_restore", provider: prov === "github" || prov === "gdrive" ? prov : undefined } };
    }
    case "save_to_cloud": {
      const ck = asStr(p.content_kind) ?? "";
      const contentKind = /conversation|chat|messages|prompts/i.test(ck) ? "conversation" : "note";
      const dest = asStr(p.destination);
      const destination = dest ? (/drive|gdrive|google/i.test(dest) ? "gdrive" : /git\s*hub/i.test(dest) ? "github" : undefined) : undefined;
      return { reply: say, action: { kind: "save_to_cloud", contentKind, note: asStr(p.note) ?? asStr(p.content), destination } };
    }
    case "remember": {
      const instruction = asStr(p.instruction);
      if (!instruction) return none(say || "What should I remember?");
      return { reply: say, action: { kind: "remember", instruction } };
    }
    default:
      return none(say || HELP);
  }
}

export async function routeMessage(text: string): Promise<BrainResponse> {
  const t = text.trim();
  if (!t) return { reply: "", action: { kind: "none", message: HELP } };
  const low = t.toLowerCase();

  // 1) ADD CATEGORY — light client intent (no server call).
  const catM = t.match(/^(?:add|create|new)\s+(?:an?\s+)?categor(?:y|ies)\s+["']?(.+?)["']?\s*$/i);
  if (catM) {
    let name = catM[1].trim();
    const hex = name.match(/#[0-9a-fA-F]{6}/);
    const color = hex ? hex[0] : undefined;
    name = name.replace(/\s*#[0-9a-fA-F]{6}\s*/, "").replace(/\s+color\s+\w+$/i, "").trim();
    if (!name) return { reply: "", action: { kind: "none", message: "What should the category be called?" } };
    return { reply: "", action: { kind: "add_category", name, color } };
  }

  // 2) ADD EXPENSE — free text or pasted receipt → existing /structure endpoint.
  const addM = t.match(/^(?:add|log|record|new)\s+(?:an?\s+)?expenses?[:\-\s]+([\s\S]+)$/i)
    || t.match(/^(?:i\s+)?(?:just\s+)?(?:spent|paid)\s+([\s\S]+)$/i);
  if (addM) {
    const payload = addM[1].trim();
    const r = await structureReceipt(payload);
    if (!r.amount_cents) {
      return { reply: "", action: { kind: "none", message: 'I couldn\'t find an amount. Try "add expense Starbucks $4.50 today".' } };
    }
    const preface = r.confidence < 0.6 ? "Low confidence — double-check the details in the ledger.\n" : "";
    return {
      reply: preface.trim(),
      action: { kind: "add_expense", vendor: r.vendor, amount_cents: r.amount_cents, date_on: r.date_on, category: r.category },
    };
  }

  // 3) SUMMARIZE — local dashboard summary (free).
  if (/\b(summar(?:y|ise|ize)|overview|recap|how\s+am\s+i\s+doing)\b/i.test(low)) {
    return { reply: "", action: { kind: "summarize" } };
  }

  // 4) DELETE — resolve the target locally; the UI asks for confirmation before executing.
  const delM = t.match(/^(?:delete|remove)\s+(?:the\s+)?(?:expense\s+)?(?:for\s+|from\s+)?([\s\S]+)$/i);
  if (delM) {
    const term = delM[1].trim();
    if (/^categor(y|ies)\b/i.test(term)) {
      return { reply: "", action: { kind: "none", message: "Categories are removed from the Categories panel, not the chat." } };
    }
    const rows = await findByVendor(term);
    if (rows.length === 0) return { reply: "", action: { kind: "none", message: `I couldn't find an expense matching "${term}".` } };
    if (rows.length > 1) {
      return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching "${term}" — be more specific:\n${pickList(rows)}` } };
    }
    const e = rows[0];
    const money = new Intl.NumberFormat(undefined, { style: "currency", currency: e.currency, currencyDisplay: "narrowSymbol" }).format(e.amount_cents / 100);
    return { reply: "", action: { kind: "delete_expense", id: e.id, label: `${e.vendor} (${money})` } };
  }

  // 5) EDIT — status flip / re-categorize / change amount. Single-match resolution only.
  const edit = await routeEdit(t);
  if (edit) return edit;

  // 6) FILTER / SEARCH — query-shaped → existing /query endpoint returns an ExpenseFilter.
  if (QUERY_RE.test(low)) {
    const cats = await store.listCategories();
    const filter = await parseExpenseQuery(t, cats.map((c) => c.name), todayISO());
    return { reply: "", action: { kind: "filter", filter, label: t } };
  }

  // 7) Unclear → a short clarifying nudge.
  return { reply: "", action: { kind: "none", message: HELP } };
}

// EDIT intents. Returns null if the message isn't an edit so the router can fall through.
async function routeEdit(t: string): Promise<BrainResponse | null> {
  // "approve X" / "reject X"
  let m = t.match(/^(approve|reject)\s+(.+)$/i);
  if (m) return resolveEdit(m[2], { status: m[1].toLowerCase() === "approve" ? "approved" : "rejected" });

  // "mark X as approved|rejected|pending" / "X as approved"
  m = t.match(/^(?:mark\s+)?(.+?)\s+as\s+(approved|rejected|pending)\s*$/i);
  if (m) return resolveEdit(m[1], { status: m[2].toLowerCase() as "approved" | "rejected" | "pending" });

  // "set/change X('s) category to Y" / "categorize X as Y"
  m = t.match(/^(?:set|change)\s+(.+?)(?:'s)?\s+category\s+to\s+(.+)$/i)
    || t.match(/^categori[sz]e\s+(.+?)\s+as\s+(.+)$/i);
  if (m) {
    const catName = m[2].trim().replace(/[.?!]+$/, "");
    const cats = await store.listCategories();
    const cat = cats.find((c) => c.name.toLowerCase() === catName.toLowerCase());
    if (!cat) return { reply: "", action: { kind: "none", message: `There's no "${catName}" category yet — add it first with "add a category ${catName}".` } };
    return resolveEdit(m[1], { category_id: cat.id }, `category → ${cat.name}`);
  }

  // "set/change X('s) amount to $Y"
  m = t.match(/^(?:set|change)\s+(.+?)(?:'s)?\s+amount\s+to\s+\$?([\d.,]+)/i);
  if (m) {
    const cents = parseCents(m[2]);
    if (cents === null) return null;
    return resolveEdit(m[1], { amount_cents: cents });
  }

  return null;
}

async function resolveEdit(term: string, patch: import("../store").ExpensePatch, note?: string): Promise<BrainResponse> {
  const rows = await findByVendor(term);
  if (rows.length === 0) return { reply: "", action: { kind: "none", message: `I couldn't find an expense matching "${term.trim()}".` } };
  if (rows.length > 1) {
    return { reply: "", action: { kind: "none", message: `I found ${rows.length} matching "${term.trim()}" — be more specific:\n${pickList(rows)}` } };
  }
  const e = rows[0];
  const label = note ? `${e.vendor} (${note})` : e.vendor;
  return { reply: "", action: { kind: "edit_expense", id: e.id, patch, label } };
}

// Resolve a library title to exactly one item, or explain why not (same 0/1/many
// contract the app uses for its own entries).
function resolveLibraryItem(term: string | undefined): { item?: import("../library").LibraryItem; problem?: string } {
  if (!term) return { problem: "Which saved item do you mean? Give me its title." };
  const found = libraryFind(term);
  if (found.length === 0) return { problem: `I couldn't find "${term}" in your library.` };
  if (found.length > 1) return { problem: `I found ${found.length} matching "${term}" — be more specific: ${found.map((f) => `"${f.title}"`).join(", ")}` };
  return { item: found[0] };
}
